import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  X,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Copy,
  Check,
  MessageCircle,
  MessageSquare,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiDocketAuditModal({ isOpen, onClose, docket, onOpenChatThread }) {
  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftMessage, setDraftMessage] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen || !docket) {
      setAuditData(null);
      setDraftMessage(null);
      return;
    }
    runAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docket]);

  const runAudit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/audit-docket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ docket_id: docket.id, docket_data: docket })
      });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
      }
    } catch (e) {
      console.error('Failed to run AI audit:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDraftUpdate = async (channel = 'whatsapp') => {
    setDrafting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/ai/draft-communication`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ docket_data: docket, channel })
      });
      if (res.ok) {
        const data = await res.json();
        setDraftMessage(data.draft);
      }
    } catch (e) {
      console.error('Failed to draft update:', e);
    } finally {
      setDrafting(false);
    }
  };

  const copyToClipboard = () => {
    if (draftMessage) {
      navigator.clipboard.writeText(draftMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen || !docket) return null;

  const score = auditData?.quality_score ?? 100;
  const scoreColor = score >= 85 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : (score >= 60 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-rose-600 bg-rose-50 border-rose-200');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center text-white shadow-lg">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base">AI Copilot & Compliance Audit</h3>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-white/10 text-white border border-white/20">
                  {docket.docket_number}
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-0.5">
                {docket.origin_city || 'Origin'} ➔ {docket.destination_city || 'Dest'} | {docket.total_actual_weight || 0} kg | {docket.status || 'Active'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-slate-300 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-sm font-semibold text-slate-700">Auditing docket parameters with AI Engine...</p>
              <p className="text-xs text-gray-400">Checking weight ratios, DG classifications, cold chain loggers, and route compliance.</p>
            </div>
          ) : (
            <>
              {/* Score & Risk Badge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className={`p-4 rounded-2xl border flex items-center justify-between ${scoreColor}`}>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider opacity-80">Docket Quality Score</p>
                    <p className="text-3xl font-black mt-0.5">{score} / 100</p>
                  </div>
                  <ShieldCheck className="w-10 h-10 opacity-75" />
                </div>

                <div className="p-4 rounded-2xl border border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Compliance Risk Level</p>
                    <p className="text-xl font-bold text-slate-800 capitalize mt-0.5">
                      {auditData?.risk_level || 'Low'} Risk
                    </p>
                  </div>
                  <span className={`w-3.5 h-3.5 rounded-full ${auditData?.risk_level === 'high' ? 'bg-rose-500' : (auditData?.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500')} animate-pulse`} />
                </div>
              </div>

              {/* Errors & Warnings */}
              {((auditData?.errors && auditData.errors.length > 0) || (auditData?.warnings && auditData.warnings.length > 0)) && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Audit Observations & Warnings
                  </h4>
                  {auditData.errors.map((err, i) => (
                    <div key={`err-${i}`} className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-xs text-rose-800 font-medium">
                      <XCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <span>{err}</span>
                    </div>
                  ))}
                  {auditData.warnings.map((warn, i) => (
                    <div key={`warn-${i}`} className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 font-medium">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <span>{warn}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Compliance Checklist */}
              {auditData?.compliance_checklist && auditData.compliance_checklist.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    Operational Compliance Checklist
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {auditData.compliance_checklist.map((c, i) => (
                      <div key={i} className="p-2.5 rounded-xl border border-gray-200 flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700">{c.item}</span>
                        {c.status === 'passed' ? (
                          <span className="flex items-center gap-1 text-emerald-600 font-bold">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Passed
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600 font-bold">
                            <AlertTriangle className="w-3.5 h-3.5" /> Needs Review
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Communication Drafter */}
              <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-indigo-600" />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-950">
                      Draft Customer Milestone Update (AI)
                    </h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDraftUpdate('whatsapp')}
                      disabled={drafting}
                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-all"
                    >
                      {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'WhatsApp Draft'}
                    </button>
                    <button
                      onClick={() => handleDraftUpdate('email')}
                      disabled={drafting}
                      className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition-all"
                    >
                      {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Email Draft'}
                    </button>
                  </div>
                </div>

                {draftMessage && (
                  <div className="relative bg-white rounded-xl p-3 border border-indigo-200 shadow-sm animate-in fade-in">
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {draftMessage}
                    </pre>
                    <button
                      onClick={copyToClipboard}
                      className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-[11px] font-semibold text-slate-700 transition-colors"
                    >
                      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      <span>{copied ? 'Copied!' : 'Copy Text'}</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={() => {
              onClose();
              if (onOpenChatThread) {
                onOpenChatThread(docket);
              } else {
                window.dispatchEvent(new CustomEvent('open-chat'));
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
          >
            <MessageSquare className="w-4 h-4 text-emerald-400" />
            <span>Open Docket Thread in Team Chat</span>
          </button>

          <button
            onClick={onClose}
            className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 text-slate-700 rounded-xl text-xs font-bold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
